import { getDataSource } from '@/lib/data-source';

export type CreditLifecycleMode = 'accumulate' | 'reset';
export type CreditExpiryMode = 'fifo' | 'lifo' | 'pro_rata';
export type CreditAllocationMethod = 'immediate' | 'scheduled';
export type UsageTrackingMethod = 'api_calls' | 'data_volume' | 'time';
export type NotificationChannel = 'email' | 'webhook' | 'dashboard';

export interface AccumulationSettings {
  expiry_billing_cycles: number;
  expiry_mode: CreditExpiryMode;
  max_accumulation_months: number;
}

export interface AutoTopupSettings {
  enabled: boolean;
  threshold_percentage: number;
  topup_amount: number;
  max_topups_per_cycle: number;
}

export interface CreditResetSchedule {
  next_reset_date: string;
  last_reset_date: string;
  timezone: string;
}

export interface UsageMetrics {
  track_endpoint_usage: boolean;
  track_data_volume: boolean;
}

// API Response interface that supports both kebab-case and snake_case
export interface CreditManagementApiResponse {
  id: string;
  lifecycle_mode?: CreditLifecycleMode;
  'lifecycle-mode'?: CreditLifecycleMode;
  expiry_cycles?: number;
  accumulation_settings?: AccumulationSettings;
  'accumulation-settings'?: {
    'expiry-billing-cycles'?: number;
    'expiry-mode'?: CreditExpiryMode;
    'max-accumulation-months'?: number;
  };
  auto_topup?: AutoTopupSettings;
  'auto-topup'?: AutoTopupSettings;
  credit_reset_schedule?: CreditResetSchedule;
  'credit-reset-schedule'?: CreditResetSchedule;
  allocation_method?: CreditAllocationMethod;
  'allocation-method'?: CreditAllocationMethod;
  allocation_schedule?: object;
  'allocation-schedule'?: object;
  prorate_credits?: boolean;
  'prorate-credits'?: boolean;
  allow_credit_transfer?: boolean;
  'allow-credit-transfer'?: boolean;
  transfer_fee_percentage?: number;
  'transfer-fee-percentage'?: string;
  min_transfer_amount?: number;
  'min-transfer-amount'?: number;
  max_transfer_amount?: number;
  'max-transfer-amount'?: number;
  track_credit_usage?: boolean;
  'track-credit-usage'?: boolean;
  usage_tracking_method?: UsageTrackingMethod;
  'usage-tracking-method'?: UsageTrackingMethod;
  usage_metrics?: UsageMetrics;
  'usage-metrics'?: UsageMetrics;
  notify_on_low_credits?: boolean;
  'notify-on-low-credits'?: boolean;
  low_credit_threshold?: number;
  'low-credit-threshold'?: number;
  notification_channels?: NotificationChannel[];
  'notification-channels'?: NotificationChannel[];
  credit_display_name?: string;
  'credit-display-name'?: string;
  credit_unit_name?: string;
  'credit-unit-name'?: string;
  credit_unit_plural?: string;
  'credit-unit-plural'?: string;
  show_expiry_dates?: boolean;
  'show-expiry-dates'?: boolean;
  created_at?: string;
  'created-at'?: string;
  updated_at?: string;
  'updated-at'?: string;
}

export interface CreditManagementSettings {
  id: string;
  lifecycle_mode: CreditLifecycleMode;
  expiry_cycles?: number; // Added for component compatibility
  accumulation_settings?: AccumulationSettings;
  auto_topup: AutoTopupSettings;
  credit_reset_schedule?: CreditResetSchedule;
  allocation_method: CreditAllocationMethod;
  allocation_schedule?: object;
  prorate_credits: boolean;
  allow_credit_transfer: boolean;
  transfer_fee_percentage?: number;
  min_transfer_amount?: number;
  max_transfer_amount?: number;
  track_credit_usage: boolean;
  usage_tracking_method: UsageTrackingMethod;
  usage_metrics?: UsageMetrics;
  notify_on_low_credits: boolean;
  low_credit_threshold?: number;
  notification_channels: NotificationChannel[];
  credit_display_name: string;
  credit_unit_name: string;
  credit_unit_plural: string;
  show_expiry_dates: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreditManagementCreateData {
  lifecycle_mode: CreditLifecycleMode;
  expiry_cycles?: number;
  accumulation_settings?: Partial<AccumulationSettings>;
  auto_topup: Partial<AutoTopupSettings>;
  allocation_method?: CreditAllocationMethod;
  allocation_schedule?: object;
  prorate_credits?: boolean;
  allow_credit_transfer?: boolean;
  transfer_fee_percentage?: number;
  min_transfer_amount?: number;
  max_transfer_amount?: number;
  track_credit_usage?: boolean;
  usage_tracking_method?: UsageTrackingMethod;
  usage_metrics?: Partial<UsageMetrics>;
  notify_on_low_credits?: boolean;
  low_credit_threshold?: number;
  notification_channels?: NotificationChannel[];
  credit_display_name?: string;
  credit_unit_name?: string;
  credit_unit_plural?: string;
  show_expiry_dates?: boolean;
}

export interface CreditManagementUpdateData extends Partial<CreditManagementCreateData> {}

export interface CreditImpactPreviewData {
  lifecycle_mode?: CreditLifecycleMode;
  expiry_cycles?: number;
  accumulation_settings?: Partial<AccumulationSettings>;
  auto_topup?: Partial<AutoTopupSettings>;
}

export interface CreditImpactPreview {
  message: string;
  // This would be expanded based on actual API implementation
}

export interface CreditManagementResponse {
  data: CreditManagementApiResponse;
  jsonapi: {
    version: string;
  };
}

export interface CreditImpactPreviewResponse {
  data: CreditImpactPreview;
  jsonapi: {
    version: string;
  };
}

class CreditManagementService {
  private dataSource = getDataSource();

  async getCreditManagementSettings(productId: string): Promise<CreditManagementApiResponse> {
    try {
      const response = await this.dataSource.get<CreditManagementResponse>(
        `/api/v1/products/${productId}/billing/credit-management-settings`
      );
      // Return the deserialized response as-is (supports both kebab and snake case)
      return response.data as unknown as CreditManagementApiResponse;
    } catch (error) {
      console.error(`Error fetching credit management settings for product ${productId}:`, error);
      throw error;
    }
  }

  async createCreditManagementSettings(
    productId: string,
    data: CreditManagementCreateData
  ): Promise<CreditManagementApiResponse> {
    try {
      const requestBody = {
        data: {
          type: 'credit-management-settings',
          attributes: {
            'lifecycle-mode': data.lifecycle_mode,
            ...(data.accumulation_settings && {
              'accumulation-settings': {
                'expiry-billing-cycles': data.accumulation_settings.expiry_billing_cycles,
                'expiry-mode': data.accumulation_settings.expiry_mode,
                'max-accumulation-months': data.accumulation_settings.max_accumulation_months
              }
            }),
            'auto-topup': {
              enabled: data.auto_topup.enabled ?? false,
              'threshold-percentage': data.auto_topup.threshold_percentage,
              'topup-amount': data.auto_topup.topup_amount,
              'max-topups-per-cycle': data.auto_topup.max_topups_per_cycle
            },
            'allocation-method': data.allocation_method || 'immediate',
            'allocation-schedule': data.allocation_schedule,
            'prorate-credits': data.prorate_credits ?? true,
            'allow-credit-transfer': data.allow_credit_transfer ?? false,
            'transfer-fee-percentage': data.transfer_fee_percentage,
            'min-transfer-amount': data.min_transfer_amount,
            'max-transfer-amount': data.max_transfer_amount,
            'track-credit-usage': data.track_credit_usage ?? true,
            'usage-tracking-method': data.usage_tracking_method || 'api_calls',
            ...(data.usage_metrics && {
              'usage-metrics': {
                'track-endpoint-usage': data.usage_metrics.track_endpoint_usage,
                'track-data-volume': data.usage_metrics.track_data_volume
              }
            }),
            'notify-on-low-credits': data.notify_on_low_credits ?? true,
            'low-credit-threshold': data.low_credit_threshold,
            'notification-channels': data.notification_channels || ['email'],
            'credit-display-name': data.credit_display_name || 'API Credits',
            'credit-unit-name': data.credit_unit_name || 'credit',
            'credit-unit-plural': data.credit_unit_plural || 'credits',
            'show-expiry-dates': data.show_expiry_dates ?? true
          }
        }
      };

      const response = await this.dataSource.post<CreditManagementResponse>(
        `/api/v1/products/${productId}/billing/credit-management-settings`,
        requestBody
      );
      return response.data.data as unknown as CreditManagementApiResponse;
    } catch (error) {
      console.error(`Error creating credit management settings for product ${productId}:`, error);
      throw error;
    }
  }

  async updateCreditManagementSettings(
    productId: string,
    data: CreditManagementUpdateData
  ): Promise<CreditManagementApiResponse> {
    try {
      // Get existing settings to obtain the ID
      const existingSettings = await this.getCreditManagementSettings(productId);
      
      const requestBody = {
        data: {
          type: 'credit-management-settings',
          id: existingSettings.id,
          attributes: {
            ...(data.lifecycle_mode && { 'lifecycle-mode': data.lifecycle_mode }),
            ...(data.accumulation_settings && {
              'accumulation-settings': {
                'expiry-billing-cycles': data.accumulation_settings.expiry_billing_cycles,
                'expiry-mode': data.accumulation_settings.expiry_mode,
                'max-accumulation-months': data.accumulation_settings.max_accumulation_months
              }
            }),
            ...(data.auto_topup && {
              'auto-topup': {
                enabled: data.auto_topup.enabled,
                'threshold-percentage': data.auto_topup.threshold_percentage,
                'topup-amount': data.auto_topup.topup_amount,
                'max-topups-per-cycle': data.auto_topup.max_topups_per_cycle
              }
            }),
            ...(data.allocation_method && { 'allocation-method': data.allocation_method }),
            ...(data.allocation_schedule !== undefined && { 'allocation-schedule': data.allocation_schedule }),
            ...(data.prorate_credits !== undefined && { 'prorate-credits': data.prorate_credits }),
            ...(data.allow_credit_transfer !== undefined && { 'allow-credit-transfer': data.allow_credit_transfer }),
            ...(data.transfer_fee_percentage !== undefined && { 'transfer-fee-percentage': data.transfer_fee_percentage }),
            ...(data.min_transfer_amount !== undefined && { 'min-transfer-amount': data.min_transfer_amount }),
            ...(data.max_transfer_amount !== undefined && { 'max-transfer-amount': data.max_transfer_amount }),
            ...(data.track_credit_usage !== undefined && { 'track-credit-usage': data.track_credit_usage }),
            ...(data.usage_tracking_method && { 'usage-tracking-method': data.usage_tracking_method }),
            ...(data.usage_metrics && {
              'usage-metrics': {
                'track-endpoint-usage': data.usage_metrics.track_endpoint_usage,
                'track-data-volume': data.usage_metrics.track_data_volume
              }
            }),
            ...(data.notify_on_low_credits !== undefined && { 'notify-on-low-credits': data.notify_on_low_credits }),
            ...(data.low_credit_threshold !== undefined && { 'low-credit-threshold': data.low_credit_threshold }),
            ...(data.notification_channels && { 'notification-channels': data.notification_channels }),
            ...(data.credit_display_name && { 'credit-display-name': data.credit_display_name }),
            ...(data.credit_unit_name && { 'credit-unit-name': data.credit_unit_name }),
            ...(data.credit_unit_plural && { 'credit-unit-plural': data.credit_unit_plural }),
            ...(data.show_expiry_dates !== undefined && { 'show-expiry-dates': data.show_expiry_dates })
          }
        }
      };

      const response = await this.dataSource.patch<CreditManagementResponse>(
        `/api/v1/products/${productId}/billing/credit-management-settings`,
        requestBody
      );
      return response.data as unknown as CreditManagementApiResponse;
    } catch (error) {
      console.error(`Error updating credit management settings for product ${productId}:`, error);
      throw error;
    }
  }

  async previewCreditImpact(
    productId: string,
    data: CreditImpactPreviewData
  ): Promise<CreditImpactPreview> {
    try {
      const requestBody = {
        data: {
          type: 'credit-impact-previews',
          attributes: {
            ...(data.lifecycle_mode && { 'lifecycle-mode': data.lifecycle_mode }),
            ...(data.accumulation_settings && {
              'accumulation-settings': {
                'expiry-billing-cycles': data.accumulation_settings.expiry_billing_cycles,
                'expiry-mode': data.accumulation_settings.expiry_mode,
                'max-accumulation-months': data.accumulation_settings.max_accumulation_months
              }
            }),
            ...(data.auto_topup && {
              'auto-topup': {
                enabled: data.auto_topup.enabled,
                'threshold-percentage': data.auto_topup.threshold_percentage,
                'topup-amount': data.auto_topup.topup_amount,
                'max-topups-per-cycle': data.auto_topup.max_topups_per_cycle
              }
            })
          }
        }
      };

      const response = await this.dataSource.post<CreditImpactPreviewResponse>(
        `/api/v1/products/${productId}/billing/credit-management-settings/preview-impact`,
        requestBody
      );
      return response.data.data;
    } catch (error) {
      console.error(`Error previewing credit impact for product ${productId}:`, error);
      throw error;
    }
  }
}

export const creditManagementService = new CreditManagementService();